/** @format */

import { FontAwesomeIcon } from '@fortawesome/react-fontawesome';
import ChevronLeftIcon from '@mui/icons-material/ChevronLeft';
import ChevronRightIcon from '@mui/icons-material/ChevronRight';
import CloseIcon from '@mui/icons-material/Close';
import ExpandMoreIcon from '@mui/icons-material/ExpandMore';
import {
  Accordion,
  AccordionDetails,
  AccordionSummary,
  Box,
  Button,
  Chip,
  IconButton,
  Link,
  Skeleton,
  Stack,
  Typography,
} from '@mui/material';
import PropTypes from 'prop-types';
import { useState } from 'react';

import { moneyFormatter } from '../../../../../utils/numbers';
import { PRODUCT_DOC_ICONS } from '../ProductDocumentActionButtons';
import {
  BrandMark,
  PriceExplanation,
  ProductVisual,
  documentUrl,
  priceText,
} from './CatalogCard';
import {
  useDetailsSchema,
  usePrice,
  useProductDetail,
  useTypologyDetail,
} from './catalogData';
import {
  DOCUMENT_TYPES,
  TYPOLOGY_CRITERIA,
  criterionLabel,
  getDetails,
  getManufacturerReference,
  getMargin,
  getPriceSource,
  getProductName,
} from './catalogLogic';
import { getDetailsLabel } from './detailsLabels';
import InfoDot from './InfoDot';

const DOC_ICONS = Object.fromEntries(
  PRODUCT_DOC_ICONS.map(({ type, icon }) => [type, icon]),
);

// Technical or pricing fields kept out of the characteristics table.
const HIDDEN_FIELDS =
  /^(id|product|created_|updated_|explanation|second_explanation|study_explanation|manufacturer_reference$)|price|cost/;

function stripHtml(html) {
  const doc = new DOMParser().parseFromString(String(html || ''), 'text/html');
  return doc.body.textContent?.trim() || '';
}

function Section({ title, children }) {
  return (
    <Box sx={{ mt: 3 }}>
      <Typography component="h3" sx={{ fontSize: 15, fontWeight: 700, mb: 1 }}>
        {title}
      </Typography>
      {children}
    </Box>
  );
}

Section.propTypes = {
  title: PropTypes.string.isRequired,
  children: PropTypes.node.isRequired,
};

function Row({ label, value }) {
  return (
    <Box
      sx={{
        display: 'flex',
        gap: 2,
        py: 1,
        borderBottom: 1,
        borderColor: 'divider',
        fontSize: 14,
      }}
    >
      <Box sx={{ color: 'text.secondary', flex: 1 }}>{label}</Box>
      <Box
        sx={{
          fontWeight: 600,
          textAlign: 'right',
          flex: 1,
          overflowWrap: 'anywhere',
        }}
      >
        {value}
      </Box>
    </Box>
  );
}

Row.propTypes = {
  label: PropTypes.node.isRequired,
  value: PropTypes.node.isRequired,
};

function CatalogProductSheet({
  product: item,
  position,
  total,
  onPrev,
  onNext,
  onClose,
  brand,
  typology,
  onOpenProduct,
  onEdit,
}) {
  const [copied, setCopied] = useState(false);
  const detail = useProductDetail(item.id, true);
  const typologyDetail = useTypologyDetail(item.typology?.slug);
  // Light index data first, full product once loaded.
  const product = detail ? { ...detail, is_active: item.is_active } : item;
  const loading = !detail;
  const price = usePrice(product, true);
  const schema = useDetailsSchema(product.typology?.slug);
  const details = getDetails(product) || {};
  const detailsKey =
    Object.keys(product).find((k) => k.endsWith('_details') && product[k]) ||
    '';
  const name = getProductName(product);
  const source = getPriceSource(product, typologyDetail?.data || typology);
  const margin =
    price?.status === 'done'
      ? getMargin(price.value, product.supplier_price)
      : null;
  const criteria = TYPOLOGY_CRITERIA[product.typology?.slug] || [];
  const tiles = [
    ...criteria,
    ...(details.guarantee != null
      ? [{ field: 'guarantee', label: 'Garantie', unit: 'ans' }]
      : []),
  ]
    .map((c) => ({
      label: c.label,
      value: criterionLabel(c, details[c.field], schema?.choices?.[c.field]),
    }))
    .filter((t) => t.value)
    .slice(0, 4);
  const characteristics = Object.entries(details).filter(
    ([field, value]) =>
      !HIDDEN_FIELDS.test(field) &&
      value !== null &&
      value !== '' &&
      typeof value !== 'undefined',
  );
  const dataSheet = product.data_sheets?.[0];
  const missingDocs = DOCUMENT_TYPES.filter(
    ({ type }) => !product[type]?.length,
  );
  const quoteDescription = stripHtml(product.quote_description);

  const copyName = () => {
    navigator.clipboard?.writeText(name).then(() => {
      setCopied(true);
      setTimeout(() => setCopied(false), 2000);
    });
  };

  return (
    <Box sx={{ display: 'flex', flexDirection: 'column', height: '100%' }}>
      <Box
        sx={{
          display: 'flex',
          alignItems: 'center',
          gap: 0.5,
          px: 1,
          py: 0.5,
          borderBottom: 1,
          borderColor: 'divider',
          position: 'sticky',
          top: 0,
          bgcolor: 'background.paper',
          zIndex: 1,
        }}
      >
        <IconButton
          aria-label="Produit précédent"
          onClick={onPrev}
          disabled={position <= 1}
          sx={{ width: 44, height: 44 }}
        >
          <ChevronLeftIcon />
        </IconButton>
        <Typography
          sx={{ fontSize: 14, color: 'text.secondary' }}
          aria-live="polite"
        >
          {position > 0 ? `${position} sur ${total}` : 'Hors de la liste'}
        </Typography>
        <IconButton
          aria-label="Produit suivant"
          onClick={onNext}
          disabled={position <= 0 || position >= total}
          sx={{ width: 44, height: 44 }}
        >
          <ChevronRightIcon />
        </IconButton>
        <IconButton
          aria-label="Fermer la fiche"
          onClick={onClose}
          sx={{ width: 44, height: 44, ml: 'auto' }}
        >
          <CloseIcon />
        </IconButton>
      </Box>

      <Box sx={{ overflowY: 'auto', p: 2, pb: 4 }}>
        <Box
          sx={{
            borderRadius: 3,
            overflow: 'hidden',
            border: 1,
            borderColor: 'grey.200',
          }}
        >
          {loading ? (
            <Skeleton variant="rectangular" height={200} animation="wave" />
          ) : (
            <ProductVisual product={product} height={200} />
          )}
        </Box>
        <Box sx={{ mt: 2 }}>
          <BrandMark brand={brand} height={28} />
        </Box>
        <Typography
          component="h2"
          sx={{ fontSize: 20, fontWeight: 700, mt: 1, lineHeight: 1.3 }}
        >
          {name}
        </Typography>
        <Stack direction="row" flexWrap="wrap" gap={1} sx={{ mt: 1.5 }}>
          <Chip
            size="small"
            color={product.is_active ? 'primary' : 'default'}
            label={product.is_active ? 'Actif' : 'Inactif'}
          />
          {product.typology?.name && (
            <Chip
              size="small"
              variant="outlined"
              label={product.typology.name}
            />
          )}
          {(product.sub_categories || []).map((sc) => (
            <Chip
              key={sc.slug}
              size="small"
              variant="outlined"
              label={sc.name}
            />
          ))}
        </Stack>
        {getManufacturerReference(product) && (
          <Typography variant="body2" color="text.secondary" sx={{ mt: 1 }}>
            Référence fabricant : {getManufacturerReference(product)}
          </Typography>
        )}

        {loading && (
          <Skeleton
            variant="rounded"
            height={140}
            sx={{ mt: 2, borderRadius: 2 }}
            aria-label="Chargement de la fiche"
          />
        )}
        {tiles.length > 0 && (
          <Box
            sx={{
              display: 'grid',
              gridTemplateColumns: 'repeat(2, minmax(0, 1fr))',
              gap: 1,
              mt: 2,
            }}
          >
            {tiles.map((t) => (
              <Box
                key={t.label}
                sx={{ bgcolor: 'grey.100', borderRadius: 2, p: 1.5 }}
              >
                <Typography sx={{ fontSize: 12, color: 'text.secondary' }}>
                  {t.label}
                </Typography>
                <Typography sx={{ fontSize: 16, fontWeight: 700 }}>
                  {t.value}
                </Typography>
              </Box>
            ))}
          </Box>
        )}

        <Box
          sx={{
            mt: 2,
            p: 2,
            borderRadius: 3,
            border: 1,
            borderColor: 'grey.300',
          }}
        >
          <Typography sx={{ fontSize: 13, color: 'text.secondary' }}>
            Prix de vente HT
          </Typography>
          <Box sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
            <Typography sx={{ fontSize: 24, fontWeight: 700 }}>
              {priceText(product, price)}
            </Typography>
            {product.is_active && (
              <InfoDot label="D'où vient ce prix ?">
                <PriceExplanation product={product} typology={typology} />
              </InfoDot>
            )}
          </Box>
          <Typography variant="body2" color="text.secondary">
            Source : {source.label}
          </Typography>
        </Box>

        <Stack direction="row" gap={1} sx={{ mt: 2 }} flexWrap="wrap">
          <Button
            variant="contained"
            disabled={!dataSheet}
            href={documentUrl(dataSheet) || undefined}
            target="_blank"
            rel="noopener noreferrer"
            sx={{ minHeight: 44, flex: 1 }}
          >
            {dataSheet ? 'Fiche technique' : 'Pas de fiche technique'}
          </Button>
          <Button
            variant="outlined"
            onClick={copyName}
            sx={{ minHeight: 44, flex: 1 }}
          >
            {copied ? 'Nom copié' : 'Copier le nom'}
          </Button>
        </Stack>

        <Accordion
          disableGutters
          variant="outlined"
          sx={{ mt: 2, borderRadius: 3, '&:before': { display: 'none' } }}
        >
          <AccordionSummary
            expandIcon={<ExpandMoreIcon />}
            sx={{ minHeight: 44 }}
          >
            <Typography sx={{ fontSize: 15, fontWeight: 600 }}>
              Prix d&apos;achat et marge
            </Typography>
          </AccordionSummary>
          <AccordionDetails>
            <Row
              label="Prix d'achat HT"
              value={
                product.supplier_price != null && product.supplier_price !== ''
                  ? `${moneyFormatter(product.supplier_price)} €`
                  : '—'
              }
            />
            {source.kind === 'coefficient' && (
              <Row
                label="Coefficient de la typologie"
                value={
                  source.coefficient != null ? `× ${source.coefficient}` : '—'
                }
              />
            )}
            {source.formula && (
              <Row
                label={source.label}
                value={<code style={{ fontSize: 12 }}>{source.formula}</code>}
              />
            )}
            <Row
              label="Marge brute HT"
              value={margin ? `${moneyFormatter(margin.margin)} €` : '—'}
            />
            <Row
              label="Taux de marque"
              value={
                margin ? `${moneyFormatter(margin.markupRate * 100)} %` : '—'
              }
            />
          </AccordionDetails>
        </Accordion>

        {product.is_kit && (product.kit_lines || []).length > 0 && (
          <Section title="Contenu du kit">
            {[...product.kit_lines]
              .sort((a, b) => (a.order ?? 0) - (b.order ?? 0))
              .map((line) => (
                <Button
                  key={line.id}
                  onClick={() => onOpenProduct(line.product)}
                  fullWidth
                  sx={{
                    justifyContent: 'flex-start',
                    textAlign: 'left',
                    minHeight: 44,
                    textTransform: 'none',
                    color: 'text.primary',
                  }}
                >
                  <Box
                    component="span"
                    sx={{ fontWeight: 700, mr: 1, color: 'text.secondary' }}
                  >
                    {line.quantity} ×
                  </Box>
                  {getProductName(line.product)}
                  <ChevronRightIcon
                    sx={{ ml: 'auto', color: 'text.secondary' }}
                  />
                </Button>
              ))}
          </Section>
        )}

        {characteristics.length > 0 && (
          <Section title="Caractéristiques">
            {characteristics.map(([field, value]) => (
              <Row
                key={field}
                label={
                  schema?.labels?.[field] || getDetailsLabel(detailsKey, field)
                }
                value={criterionLabel(null, value, schema?.choices?.[field])}
              />
            ))}
          </Section>
        )}

        {!loading && (
          <Section title="Documents">
            {DOCUMENT_TYPES.flatMap(({ type, label }) =>
              (product[type] || []).map((doc) => (
                <Link
                  key={`${type}-${doc.id}`}
                  href={documentUrl(doc)}
                  target="_blank"
                  rel="noopener noreferrer"
                  underline="hover"
                  sx={{
                    display: 'flex',
                    alignItems: 'center',
                    gap: 1.5,
                    minHeight: 44,
                    fontSize: 15,
                  }}
                >
                  <FontAwesomeIcon icon={DOC_ICONS[type]} fixedWidth />
                  <span>
                    {label} — {doc.name}
                  </span>
                </Link>
              )),
            )}
            {missingDocs.length > 0 && (
              <Typography variant="body2" color="text.secondary" sx={{ mt: 1 }}>
                Manquant :{' '}
                {missingDocs.map((d) => d.label.toLowerCase()).join(', ')}.
              </Typography>
            )}
          </Section>
        )}

        {quoteDescription && (
          <Section title="Description devis">
            <Typography variant="body2" sx={{ whiteSpace: 'pre-line' }}>
              {quoteDescription}
            </Typography>
          </Section>
        )}

        {onEdit && (
          <Box sx={{ mt: 3 }}>
            <Link
              component="button"
              type="button"
              onClick={() => onEdit(product)}
              sx={{ fontSize: 15, minHeight: 44 }}
            >
              Modifier dans l&apos;administration
            </Link>
          </Box>
        )}
      </Box>
    </Box>
  );
}

CatalogProductSheet.propTypes = {
  product: PropTypes.shape({
    id: PropTypes.number,
    typology: PropTypes.shape({
      slug: PropTypes.string,
      name: PropTypes.string,
    }),
    kit_lines: PropTypes.arrayOf(PropTypes.shape({})),
  }).isRequired,
  position: PropTypes.number.isRequired,
  total: PropTypes.number.isRequired,
  onPrev: PropTypes.func.isRequired,
  onNext: PropTypes.func.isRequired,
  onClose: PropTypes.func.isRequired,
  brand: PropTypes.shape({}),
  typology: PropTypes.shape({}),
  onOpenProduct: PropTypes.func.isRequired,
  onEdit: PropTypes.func,
};

CatalogProductSheet.defaultProps = {
  brand: null,
  typology: null,
  onEdit: undefined,
};

export default CatalogProductSheet;
